import clsx from 'clsx';
import type { FieldValues } from 'react-hook-form';

type LabelProps<_T extends FieldValues> = {
  text: string;
  subText?: string;
  subTextClassName?: string;
  htmlFor?: string;
} & React.DetailedHTMLProps<
  React.LabelHTMLAttributes<HTMLLabelElement>,
  HTMLLabelElement
>;

export function LabelT<T extends FieldValues>({
  text,
  subText,
  subTextClassName,
  htmlFor = '',
  style,
  ...restParams
}: LabelProps<T>) {
  const { className, ...rest } = restParams;
  let defaultStyles = [
    'block',
    'text-xs',
    'leading-6',
    'text-gray-600',
    'font-normal',
    'cursor-inherit',
  ];

  const subTextDefaultStyles = ['text-gray-400', 'font-light'];

  // If className contains a leading-* class, remove leading-6 from defaultStyles, otherwise the leading-6 is always overwritten the leading-* class that is coming from the parent component
  if (className?.includes('leading-')) {
    defaultStyles = defaultStyles.filter(
      (style) => !style.startsWith('leading-'),
    );
  }

  if (className?.includes('font-medium')) {
    defaultStyles = defaultStyles.filter(
      (style) => !style.startsWith('font-normal'),
    );
  }

  if (className?.includes('text-base')) {
    defaultStyles = defaultStyles.filter(
      (style) => !style.startsWith('text-sm'),
    );
  }

  // Strip the default text-gray-600 when a custom text-gray-* color is provided
  if (className?.includes('text-gray-')) {
    defaultStyles = defaultStyles.filter(
      (style) => !style.startsWith('text-gray-'),
    );
  }

  return (
    <label
      htmlFor={htmlFor}
      className={clsx([...defaultStyles, className])}
      {...rest}
      {...(style ? { style } : {})}>
      {text}{' '}
      {subText && (
        <span className={clsx([...subTextDefaultStyles, subTextClassName])}>
          {subText}
        </span>
      )}
    </label>
  );
}

export default LabelT;
